/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type RefObject, useEffect, useRef, useState, createRef } from 'react';
import root from 'window-or-global';
import { type ScrolledPartCardContainerProps, type CertonaClickItem } from '../../interface';
import Image from '@/components/NextImage';
import styles from './styles.module.scss';
import cx from 'classnames';
import azCommonStyles from '@/theme/globals.module.scss';
import { HorizontalPartCard } from '../HorizontalPartCard';
import { LargePartCard } from '../LargePartCard';
import { certonaPageType } from '@/constants/certonaPageType';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { useUtagReady } from '@/utils/analytics/useUtagReady';
import { useLabels } from '@/hooks/useLabels';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import Hidden from '@/components/Hidden';
import { routePaths } from '@/constants/routePaths';
import { getAddToCartInfo } from '../../utils/getAddToCartInfo';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { sessionStorage } from '@/utils/sessionStorage';
import { useCertonaAddToCartVisibility } from '../../hooks/useCertonaAddToCartVisibility';
import { getVisibleItemCount } from '@/utils/getVisibleItemCount';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useFeatureFlag } from '@/features/globalConfig/hooks/useFeatureFlag';

const labelMap = {
  lblDelivery: 'label_threshold_delivery',
  lblSpend: 'label_threshold_spend',
  lblThresholdContainerHeader: 'label_ThresholdContainerHeader',
};

const ScrolledPartCardContainer = (props: ScrolledPartCardContainerProps) => {
  const {
    id,
    title,
    subtitle,
    partsList,
    pricingInfo,
    contentsAligment = 'vertical',
    skuDetailsData,
    addToCartAnalyticsandSuccessMessage,
    onCertonaRecommendationClick,
    pageType,
    thresholdHeaderText = '',
    certonaType,
    shelfSeoUrl,
    setAddedCertonaItemObject,
    inDrawer,
    updateAfterAddtoCartClick,
    recTabNumber,
    attributionToken,
    certonaScheme,
    leadingItemFulfillmentId,
  } = props;

  const isOosOrDoesNotFit = certonaType === 'OOS' || certonaType === 'DNF';
  const { isUtagReady: isUtagDefined } = useUtagReady();
  const { lblThresholdContainerHeader } = useLabels(labelMap);
  const router = useLegacyRouter();
  const isPDP = router.pathname === '/products/[...slug]';
  const isHome = router.pathname === '/';
  const isShelf = router.pathname === '/[...seoUrlPath]';
  const isCart = router.pathname === '/cart';
  const isVehicleDetailsPage = router.pathname.includes('vehicleDetails');
  const isRecommendationsLandingPage = router.asPath.includes(
    routePaths.landingPageRecommendations,
  );
  const isSearchPage = router.pathname.includes('search');
  const [selectedPartCardIndex, setSelectedPartCardIndex] = useState(0);
  const [lastVisibleCardIndex, setLastVisibleCardIndex] = useState(0);
  const [hideLeftArrow, setHideLeftArrow] = useState(true);
  const [hideRightArrow, setHideRightArrow] = useState(false);
  const partListRefs = useRef<Array<RefObject<HTMLLIElement>>>([]);
  const unorderedListElement: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement | null>(
    null,
  );
  const isCertonaAddToCartCTAEnabled = useCertonaAddToCartFlag();
  const showAddToCartCTA =
    isCart || isVehicleDetailsPage || isRecommendationsLandingPage || isCertonaAddToCartCTAEnabled;
  const preferredVehicleId = usePreferredVehicle();
  const vehicleName = preferredVehicleId ? preferredVehicleId.make : 'Vehicle';
  const isDefaultTitle = subtitle === '' && title !== '';

  const skuIds = partsList.filter((part) => !!part.partgroup_id).map((part) => part.id);
  const { shouldShowAddToCart, skuConfigs, isLoading } = useCertonaAddToCartVisibility(skuIds);
  const isDealsEnabled = useFeatureFlag('IS_DEALS_PRODUCT_RECS_ENABLED') === 'true';
  const [visibleDealSkuIds, setVisibleDealSkuIds] = useState<string[]>([]);
  const [carouselNearViewport, setCarouselNearViewport] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCarouselNearViewport(entry.isIntersecting);
      },
      { rootMargin: '250px' },
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const { data: dealsData, isLoading: isDealsLoading } = useProductSkuDeals({
    skuIds: visibleDealSkuIds,
    enabled: isDealsEnabled && visibleDealSkuIds.length > 0 && carouselNearViewport,
  });
  const trackCertonaLinkInteractionBySection = (linkName: string, section: string, id: string) => {
    // TODO - Figure out lint error to and remove eslint comments
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    const utag = root.utag_data;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    const sectionName = `${section ?? utag?.pageType}_certona`;
    const data = {
      productLinkName: linkName,
      clickedProductId: id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      pageName: utag?.pageName ?? '',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      pageType: utag?.pageType ?? '',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      siteSection: utag?.siteSection ?? '',
      eventType: sectionName.replace(/\s/g, '_').toLowerCase(),
    };
    clickTrack(data);
  };

  const handleCertonaClick = (linkName: string, id: string, item?: CertonaClickItem) => {
    onCertonaRecommendationClick?.();
    root.sessionStorage.setItem('productType', 'certona');
    const token = item?.attributionToken || attributionToken;
    if (token) {
      sessionStorage.setItem('googleRetailRecommendationAttributionToken', token);
    }
    if (isUtagDefined === undefined && pageType !== certonaPageType.shoppingCartPage) {
      trackCertonaLinkInteractionBySection(linkName, pageType, id);
    }
  };

  useEffect(() => {
    const scrollBlockScrolled = () => {
      if (unorderedListElement.current && partListRefs.current[partListRefs.current.length - 1]) {
        if (
          partListRefs.current[partListRefs.current.length - 1]?.current?.getBoundingClientRect()
            .right -
            1 >
          unorderedListElement.current.getBoundingClientRect().right
        ) {
          setHideRightArrow(false);
        } else {
          setHideRightArrow(true);
        }
      }

      if (unorderedListElement.current && partListRefs.current[0]) {
        if (
          partListRefs.current[0]?.current?.getBoundingClientRect().left <
          unorderedListElement.current.getBoundingClientRect().left
        ) {
          setHideLeftArrow(false);
        } else {
          setHideLeftArrow(true);
        }
      }

      const scrollLeft = unorderedListElement.current?.scrollLeft ?? 0;
      const clientWidth = unorderedListElement.current?.clientWidth ?? 0;
      const scrollRight = scrollLeft + clientWidth;

      let firstVisibleIndex = 0;
      let lastVisibleIndex = 0;
      let foundFirstVisible = false;

      for (let i = 0; i < partListRefs.current.length; i++) {
        if (!partListRefs.current[i]?.current) {
          continue;
        }

        const card = partListRefs.current[i].current;
        const cardLeft = card.offsetLeft;
        const cardRight = cardLeft + card.offsetWidth;

        if (cardRight > scrollLeft && !foundFirstVisible) {
          firstVisibleIndex = i;
          foundFirstVisible = true;
        }

        if (cardLeft < scrollRight) {
          lastVisibleIndex = i;
        }
      }

      setSelectedPartCardIndex(firstVisibleIndex);
      setLastVisibleCardIndex(lastVisibleIndex);
    };

    let ulElementRefValue: HTMLUListElement | null = null;

    if (unorderedListElement.current) {
      ulElementRefValue = unorderedListElement.current;
      unorderedListElement.current.addEventListener('scroll', scrollBlockScrolled);
      window.addEventListener('resize', scrollBlockScrolled);
    }

    scrollBlockScrolled();

    return () => {
      if (ulElementRefValue) {
        ulElementRefValue.removeEventListener('scroll', scrollBlockScrolled);
        window.removeEventListener('resize', scrollBlockScrolled);
      }
    };
  }, [partsList]);

  const getVisibleCardCount = () => {
    const list = unorderedListElement.current;
    const items = partListRefs.current;
    if (!list || !items.length) {
      return 1;
    }

    const scrollLeft = list.scrollLeft;
    let firstVisibleIndex = 0;
    for (let i = 0; i < items.length; i++) {
      if (
        items[i]?.current &&
        items[i].current.offsetLeft + items[i].current.offsetWidth > scrollLeft
      ) {
        firstVisibleIndex = i;
        break;
      }
    }

    const origin = items[firstVisibleIndex]?.current?.offsetLeft ?? 0;
    const visibleItems = items.slice(firstVisibleIndex);
    const itemExtents = visibleItems.map((itemRef) => {
      const card = itemRef.current;
      if (!card) {
        return 0;
      }
      return card.offsetLeft - origin + card.offsetWidth;
    });

    return getVisibleItemCount(list.getBoundingClientRect().width, itemExtents);
  };

  const handleLeftClick = () => {
    if (
      partListRefs.current.length &&
      unorderedListElement.current &&
      partListRefs.current[0]?.current?.getBoundingClientRect()?.left <
        unorderedListElement.current.getBoundingClientRect().left
    ) {
      const partCards = partListRefs.current;
      const step = Math.max(getVisibleCardCount() - 1, 1);
      const targetIndex = Math.max(selectedPartCardIndex - step, 0);
      const targetPartCard = partCards[targetIndex];

      unorderedListElement.current.scrollTo({
        left: targetPartCard.current.offsetLeft - 6,
        top: 0,
        behavior: 'smooth',
      });
      setSelectedPartCardIndex(targetIndex);
    }
  };

  const handleRightClick = () => {
    if (
      partListRefs.current.length &&
      unorderedListElement.current &&
      partListRefs.current[partListRefs.current.length - 1].current.getBoundingClientRect().right >
        unorderedListElement.current.getBoundingClientRect().right
    ) {
      const partCards = partListRefs.current;
      const step = Math.max(getVisibleCardCount() - 1, 1);
      const targetIndex = Math.min(selectedPartCardIndex + step, partListRefs.current.length - 1);
      const targetPartCard = partCards[targetIndex];
      unorderedListElement.current.scrollTo({
        left: targetPartCard.current.offsetLeft - 6,
        top: 0,
        behavior: 'smooth',
      });
      setSelectedPartCardIndex(targetIndex);
    }
  };

  if (partsList.length && partListRefs.current.length !== partsList.length) {
    partListRefs.current = Array(partsList.length)
      .fill(null)
      .map((_, i) => partListRefs.current[i] || createRef());
  }

  useEffect(() => {
    if (!isDealsEnabled || !partListRefs.current.length) {
      setVisibleDealSkuIds([]);
      return;
    }

    const endIndex = Math.min(lastVisibleCardIndex + 1, partsList.length);
    const visibleItems = partsList.slice(selectedPartCardIndex, endIndex);
    const skuIds = visibleItems?.filter((item) => item.isDeal)?.map((item) => item.id) ?? [];

    setVisibleDealSkuIds(skuIds);
  }, [selectedPartCardIndex, lastVisibleCardIndex, partsList, isDealsEnabled]);

  const newTitle =
    !!title && title.toUpperCase().includes('RECOMMENDED FOR YOU') && !!preferredVehicleId
      ? `Recommended For Your ${vehicleName}`
      : title;

  return partsList.length > 0 ? (
    <div
      ref={carouselRef}
      data-testid="carousel"
      className={cx(
        isPDP && isOosOrDoesNotFit && !(contentsAligment === 'vertical')
          ? styles.swipeableContainerForOos
          : styles.swipeableContainer,
        {
          [styles.thresholdContainer]: id === 'certona-threshold',
        },
      )}
    >
      {!subtitle && (!!title || !!thresholdHeaderText) && (
        <>
          <h2
            className={cx(styles.partTitle, azCommonStyles['az-padding-bottom-4xs'])}
            data-testid="dynamic-content"
          >
            {thresholdHeaderText ? lblThresholdContainerHeader : newTitle}
          </h2>
          <div className={styles.separator} />
        </>
      )}

      {!!subtitle && <h2 className={styles.thresholdHeaderText}>{subtitle}</h2>}

      <div
        className={cx(
          styles.scrollingContainer,
          contentsAligment === 'vertical'
            ? styles.verticalScrollBlock
            : styles.horizontalScrollBlock,
          {
            [styles.marginForThresholdHeader]: !!thresholdHeaderText,
            [styles.oosNoMargin]: isPDP && isOosOrDoesNotFit && !(contentsAligment === 'vertical'),
            [styles.scrollingContainerOverflow]:
              isPDP && isOosOrDoesNotFit && !(contentsAligment === 'vertical'),
          },
        )}
      >
        <Hidden smDown>
          <div
            role="button"
            data-testid="carousel-left-arrow"
            className={cx(
              styles.leftArrow,
              {
                [styles.hiddenArrow]: hideLeftArrow,
                [styles.arrowAlignement]: contentsAligment !== 'vertical' && !isOosOrDoesNotFit,
                [styles.oosArrowAlignment]: contentsAligment !== 'vertical' && isOosOrDoesNotFit,
              },
              contentsAligment === 'vertical'
                ? styles.leftArrowVertical
                : styles.leftArrowHorizontal,
            )}
            tabIndex={0}
            onClick={handleLeftClick}
            onKeyDown={(e) => {
              if ((e || window.event).keyCode == 13) {
                handleLeftClick();
              }
            }}
          >
            <Image
              className={styles.chevronImg}
              src="/images/arrow_right_initial.svg"
              alt="scroll carousel left"
              height={64}
              width={57}
            />
            <Image
              className={styles.chevronImgHover}
              src="/images/arrow_right_hover.svg"
              alt=""
              height={68}
              width={57}
            />
          </div>
        </Hidden>
        <ul
          className={cx(styles.cardsList, styles.newCardsListProductDesign, styles.nativeScroll, {
            [styles.oosCardList]: isPDP && isOosOrDoesNotFit && !(contentsAligment === 'vertical'),
            [styles.cartCardList]: isCart,
            [styles.increasedPadding]: isDefaultTitle,
          })}
          ref={unorderedListElement}
        >
          {partsList.map(
            (
              {
                id,
                product_image_url,
                product_url_relative,
                product_name,
                total_review_count,
                average_rating,
                rating,
                isDeal,
                attributionToken: itemAttributionToken,
                partgroup_id,
                availableFulfillmentIds,
              },
              index,
            ) => {
              // Get inventory config for this SKU from the new inventories API
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const inventoryConfig = skuConfigs[id];
              const partGroupId = partgroup_id;

              const addToCartInfo = getAddToCartInfo({
                skuDetailsData,
                id,
                inventoryConfig,
                partGroups: partGroupId,
                availableFulfillmentIds,
                leadingItemFulfillmentId,
              });
              // Determine if ATC should be shown for this specific SKU
              // Uses inventory API when available, falls back to existing logic
              const isAddToCartAreaEnabled = !!(props?.showAddToCartCTA ?? showAddToCartCTA);
              const skuShouldShowAddToCart =
                certonaType === 'CTJ' ? true : shouldShowAddToCart(id) && !!partgroup_id;

              return (
                <li
                  key={id}
                  className={
                    isPDP || isHome || isShelf || isSearchPage
                      ? styles.newCardItem
                      : styles.cardItem
                  }
                  ref={partListRefs.current[index]}
                >
                  {contentsAligment === 'vertical' ? (
                    <LargePartCard
                      key={id}
                      itemId={id}
                      part={{
                        properties: {
                          seoUrl: product_url_relative,
                          imageUrl: product_image_url,
                          makeModelYearPath: undefined,
                          refPageType: '',
                        },
                        label: product_name,
                        isDeal,
                        rating,
                        totalReviews: total_review_count,
                        averageRating: average_rating,
                      }}
                      pricingInfo={pricingInfo?.[id]}
                      addToCartData={addToCartInfo}
                      actionHandler={(linkName: string, itemId: string) =>
                        handleCertonaClick(linkName, itemId, {
                          attributionToken: itemAttributionToken,
                        })
                      }
                      addToCartAnalyticsandSuccessMessage={addToCartAnalyticsandSuccessMessage}
                      setAddedCertonaItemObject={setAddedCertonaItemObject}
                      showAddToCartCTA={isAddToCartAreaEnabled}
                      shouldShowAddToCart={skuShouldShowAddToCart}
                      isLoadingAddToCart={certonaType === 'CTJ' ? false : isLoading}
                      recTabNumber={recTabNumber}
                      certonaScheme={certonaScheme}
                      dealsData={dealsData}
                      isDealsLoading={isDealsLoading}
                      visibleDealSkuIds={visibleDealSkuIds}
                    />
                  ) : (
                    <HorizontalPartCard
                      key={id}
                      itemId={id}
                      part={{
                        properties: {
                          seoUrl: product_url_relative,
                          imageUrl: product_image_url,
                          makeModelYearPath: undefined,
                          refPageType: '',
                        },
                        label: product_name,
                        isDeal,
                        rating,
                        totalReviews: total_review_count,
                        averageRating: average_rating,
                      }}
                      pricingInfo={pricingInfo?.[id]}
                      addToCartData={addToCartInfo}
                      updateAfterAddtoCartClick={updateAfterAddtoCartClick}
                      actionHandler={(linkName: string, itemId: string) =>
                        handleCertonaClick(linkName, itemId, {
                          attributionToken: itemAttributionToken,
                        })
                      }
                      addToCartAnalyticsandSuccessMessage={addToCartAnalyticsandSuccessMessage}
                      showAddToCartCTA={isAddToCartAreaEnabled}
                      shouldShowAddToCart={skuShouldShowAddToCart}
                      isLoadingAddToCart={certonaType === 'CTJ' ? false : isLoading}
                      certonaType={certonaType}
                      lastIndex={index === partsList.length - 1}
                      partsListLength={partsList.length}
                      shelfSeoUrl={shelfSeoUrl}
                      inDrawer={inDrawer}
                      setAddedCertonaItemObject={setAddedCertonaItemObject}
                      cardStyle={'horizontal_scrolled'}
                      certonaScheme={certonaScheme}
                      dealsData={dealsData}
                      isDealsLoading={isDealsLoading}
                      visibleDealSkuIds={visibleDealSkuIds}
                    />
                  )}
                </li>
              );
            },
          )}
        </ul>
        <Hidden smDown>
          <div
            role="button"
            data-testid="carousel-right-arrow"
            className={cx(styles.rightArrow, {
              [styles.hiddenArrow]: hideRightArrow,
              [styles.arrowAlignement]: contentsAligment !== 'vertical' && !isOosOrDoesNotFit,
              [styles.oosArrowAlignment]: contentsAligment !== 'vertical' && isOosOrDoesNotFit,
            })}
            tabIndex={0}
            onClick={handleRightClick}
            onKeyDown={(e) => {
              if ((e || window.event).keyCode == 13) {
                handleRightClick();
              }
            }}
          >
            <Image
              className={styles.chevronImg}
              src="/images/arrow_right_initial.svg"
              alt="scroll carousel right"
              height={64}
              width={57}
            />
            <Image
              className={styles.chevronImgHover}
              src="/images/arrow_right_hover.svg"
              alt=""
              height={68}
              width={57}
            />
          </div>
        </Hidden>
      </div>
    </div>
  ) : null;
};

export default ScrolledPartCardContainer;
